// the following import is used to ensure the block suite editor effects are run
import '../blocksuite/block-suite-editor';

import { DebugLogger } from '@affine/debug';
import { DEFAULT_WORKSPACE_NAME } from '@affine/env/constant';
import onboardingEnUrl from '@affine/templates/onboarding-en.zip';
import onboardingEsUrl from '@affine/templates/onboarding-es.zip';
import onboardingPtUrl from '@affine/templates/onboarding-pt.zip';
import { ZipTransformer } from '@blocksuite/affine/widgets/linked-doc';

import { DocsService } from '../modules/doc';
import { I18nService } from '../modules/i18n';
import {
  getAFFiNEWorkspaceSchema,
  type WorkspacesService,
} from '../modules/workspace';

/**
 * Resolve the onboarding zip URL based on the user's AFFiNE language preference.
 * Defaults to Spanish for non-EN/PT languages.
 */
function getOnboardingUrl(languageKey?: string): string {
  if (!languageKey) return onboardingEsUrl;
  const prefix = languageKey.slice(0, 2).toLowerCase();
  if (prefix === 'en') return onboardingEnUrl;
  if (prefix === 'pt') return onboardingPtUrl;
  return onboardingEsUrl;
}

/**
 * YOD-673: the name of a member's first cloud workspace, in their language.
 *
 * Built from the account's display label. Only the first name is used, and a
 * label that looks like an email (no display name set) never ends up in the
 * workspace name — the member gets the generic "Mi workspace" instead.
 * Replaces the old fixed "yoDEV Cloud", which said nothing about whose space
 * it was.
 */
export function firstWorkspaceName(
  label?: string | null,
  languageKey?: string
): string {
  const prefix = (languageKey || 'es').slice(0, 2).toLowerCase();
  const raw = (label || '').trim();
  const first =
    raw && !raw.includes('@') ? raw.split(/\s+/)[0].slice(0, 40) : '';
  if (prefix === 'en') return first ? `${first}'s workspace` : 'My workspace';
  if (prefix === 'pt') return first ? `Workspace de ${first}` : 'Meu workspace';
  return first ? `Workspace de ${first}` : 'Mi workspace';
}

/** Onboarding doc titles by language */
const ONBOARDING_TITLES = ['Comenzando', 'Getting Started', 'Começando'];

const logger = new DebugLogger('createFirstAppData');

export async function buildShowcaseWorkspace(
  workspacesService: WorkspacesService,
  flavour: string,
  workspaceName: string,
  languageKey?: string,
  /**
   * YOD-512 — the id of the account creating this workspace, when there is one.
   *
   * The onboarding docs arrive via ZipTransformer from a pre-built template, so
   * they carry no `createdBy`: the zip was authored before any particular user
   * existed. That is what makes every brand-new cloud workspace satisfy
   * `missingCreatedBy` in DocCreatedByUpdatedBySyncService, which is why a
   * first-time user is greeted by the data-migration banner.
   *
   * Stamping the creator here removes the trigger, so the banner never fires
   * for a new workspace. Optional: local workspaces do not need it, since that
   * service short-circuits on `flavour !== 'local'`.
   */
  createdByUserId?: string
) {
  const onboardingUrl = getOnboardingUrl(languageKey);

  const meta = await workspacesService.create(flavour, async docCollection => {
    docCollection.meta.initialize();
    docCollection.doc.getMap('meta').set('name', workspaceName);
    const blob = await (await fetch(onboardingUrl)).blob();

    await ZipTransformer.importDocs(
      docCollection,
      getAFFiNEWorkspaceSchema(),
      blob
    );
  });

  const { workspace, dispose } = workspacesService.open({ metadata: meta });

  await workspace.engine.doc.waitForDocReady(workspace.id);

  const docsService = workspace.scope.get(DocsService);

  // YOD-512: attribute the imported template docs to whoever created the
  // workspace. Best-effort — a failure here must never block workspace
  // creation, which is the actual job of this function. The worst case if it
  // does fail is the pre-existing behaviour: the migration banner appears once.
  if (createdByUserId) {
    try {
      for (const docRecord of docsService.list.docs$.value) {
        if (!docRecord.createdBy$.value) {
          docRecord.setCreatedBy(createdByUserId);
        }
      }
    } catch (err) {
      logger.error('failed to stamp createdBy on onboarding docs', err);
    }
  }

  const defaultDoc = docsService.list.docs$.value.find(p =>
    ONBOARDING_TITLES.some(title => p.title$.value.startsWith(title))
  );

  // Wait for the root doc and imported docs to sync to the cloud before
  // disposing. Without this, dispose() kills the sync engine before the
  // imported docs are pushed, causing them to disappear on refresh.
  workspace.engine.doc.addPriority(workspace.id, 100);
  if (defaultDoc) {
    workspace.engine.doc.addPriority(defaultDoc.id, 100);
  }
  await workspace.engine.doc.waitForSynced(workspace.id);
  if (defaultDoc) {
    await workspace.engine.doc.waitForSynced(defaultDoc.id);
  }

  dispose();

  return { meta, defaultDocId: defaultDoc?.id };
}

export async function createFirstAppData(workspacesService: WorkspacesService) {
  if (localStorage.getItem('is-first-open') !== null) {
    return;
  }
  localStorage.setItem('is-first-open', 'false');
  const { meta, defaultDocId } = await buildShowcaseWorkspace(
    workspacesService,
    'local',
    DEFAULT_WORKSPACE_NAME
  );
  logger.info('create first workspace', defaultDocId);
  return { meta, defaultPageId: defaultDocId };
}
